import { permissionsSelectors } from 'ducks/permissions'
import { omit } from 'lodash'
import { BaseTheme, UserProfileGroupOutlineIcon } from 'opensolar-ui'
import { useCallback } from 'react'
import { useTranslate } from 'react-admin'
import { useSelector } from 'react-redux'
import { RootState } from 'types/state'
import type {
  ConfigurableFilterOptionsType,
  FilterComponentNodeV2,
  FilterGenericPropsType,
  FilterValuesType,
  OptionGenericType,
} from '../typeV2'
import { EXHIBITOR_FILTER_KEY, MY_LIST_FILTER_KEY, SHARED_FROM_KEY } from './constants'

export const VIEW_FROM_FILTER_NODE_KEY_V2 = 'view_From_filter_key'
const DEFAULT_OPTION = { id: 'default', title: 'All', value: '' }

const DEFAULT_OPTIONS: OptionGenericType<string>[] = [
  DEFAULT_OPTION,
  {
    id: MY_LIST_FILTER_KEY,
    value: MY_LIST_FILTER_KEY,
    title: 'My List',
  },
  {
    id: EXHIBITOR_FILTER_KEY,
    value: EXHIBITOR_FILTER_KEY,
    title: 'Showcase Partners',
  },
]

const getProjectData = () => {
  const projectForm = window.projectForm
  const formValues = projectForm?.getState().values || {}
  const org = window.reduxStore.getState().orgs?.org
  return { formValues, org }
}

const getSharedFromOrgDetails = (formValues: any, org: any) => {
  const sharedWith = formValues.shared_with
  const projectOwnerId = formValues.org_id

  if (!sharedWith || sharedWith.length === 0) {
    return null
  }

  const firstSharedOrg = sharedWith[0]
  if (!firstSharedOrg) {
    return null
  }

  let sharedFromOrgName = firstSharedOrg.org_name
  let sharedFromOrgId = firstSharedOrg.org_id

  // If current org is the same as project owner, show firstSharedOrg.org_name
  // Otherwise show project owner org name as shared from
  if (org?.id === projectOwnerId) {
    // Current org is project owner, use firstSharedOrg.org_name and org_id
    sharedFromOrgName = firstSharedOrg.org_name
    sharedFromOrgId = firstSharedOrg.org_id
  } else {
    // Current org is not project owner, use project owner org name from formValues
    sharedFromOrgName = formValues.org_name
    sharedFromOrgId = projectOwnerId
  }

  return { sharedFromOrgName, sharedFromOrgId }
}

const getDynamicDefaultValue = (formValues: any, org: any) => {
  const componentSelectorOwnerId = formValues.component_selector_owner_id

  if (!componentSelectorOwnerId) {
    // Only default to "My List" in the components selector modal context (projectForm present).
    return window.projectForm ? MY_LIST_FILTER_KEY : DEFAULT_OPTION.value
  } else if (componentSelectorOwnerId === org?.id) {
    return MY_LIST_FILTER_KEY
  } else {
    // Check if sharedWith exists to determine if SHARED_FROM_KEY should be available
    const sharedFromDetails = getSharedFromOrgDetails(formValues, org)
    return sharedFromDetails ? sharedFromDetails.sharedFromOrgId?.toString() : DEFAULT_OPTION.value
  }
}

export const createViewFromFilterNode = <T,>({
  label = 'Component Type',
  rendererComponent: RendererComponent,
  ...restConfigs
}: ConfigurableFilterOptionsType<string, string>): FilterComponentNodeV2 => {
  const FilterComponent = ({
    allFilterValues,
    modifiableFilterValues,
    setModifiableFilters,
  }: FilterGenericPropsType) => {
    // Get project data using shared utility function
    const { formValues, org } = getProjectData()

    const projectPermissionsLoaded = useSelector(permissionsSelectors.getProjectPermissionsLoaded)
    const projectPermission = useSelector((state: RootState) =>
      permissionsSelectors.getProjectPermissionByKey(state, 'components', true)
    )
    const rolePermission = useSelector((state: RootState) =>
      permissionsSelectors.getPermissionByKey(state, 'components')
    )
    const allowView = projectPermissionsLoaded ? projectPermission.allowView : rolePermission.allowView

    // Generate ViewFrom filter options with conditional "Shared from" option insertion
    const viewFromOptionsWithSharedFrom = (() => {
      let baseOptions = [...DEFAULT_OPTIONS]

      if (!allowView) {
        baseOptions = baseOptions.filter((opt) => opt.id !== 'default')
      }

      const sharedFromDetails = getSharedFromOrgDetails(formValues, org)

      // Check if project has shared_with
      if (sharedFromDetails) {
        // Find the index of EXHIBITOR_FILTER_KEY to insert above it
        const exhibitorIndex = baseOptions.findIndex((option) => option.id === EXHIBITOR_FILTER_KEY)
        if (exhibitorIndex !== -1) {
          // Insert the "Shared from" option above EXHIBITOR_FILTER_KEY
          baseOptions.splice(exhibitorIndex, 0, {
            id: SHARED_FROM_KEY,
            value: sharedFromDetails.sharedFromOrgId?.toString() || '',
            title: `Shared from ${sharedFromDetails.sharedFromOrgName}`,
            suffix: <UserProfileGroupOutlineIcon size={20} color={BaseTheme.palette.grey[350]} />,
          })
        }
      }

      return baseOptions
    })()

    // Get default ViewFrom filter value based on component selector owner and sharing status.
    // When !allowView, "All" is not available — coerce to My List.
    const resolveViewFromValue = (value: string) =>
      !allowView && value === DEFAULT_OPTION.value ? MY_LIST_FILTER_KEY : value

    const defaultViewFromValue = resolveViewFromValue(getDynamicDefaultValue(formValues, org))

    const translate = useTranslate()

    const onSelect = useCallback(
      (value, selectedOption) => {
        // Always clear first
        const newFilterValues = omit({ ...modifiableFilterValues }, [
          MY_LIST_FILTER_KEY,
          EXHIBITOR_FILTER_KEY,
          SHARED_FROM_KEY,
        ])

        if (value === '') {
          // Do nothing for empty value
        } else if (value === MY_LIST_FILTER_KEY) {
          newFilterValues[MY_LIST_FILTER_KEY] = true
        } else if (value === EXHIBITOR_FILTER_KEY) {
          newFilterValues[EXHIBITOR_FILTER_KEY] = true
        } else if (selectedOption?.id === SHARED_FROM_KEY) {
          newFilterValues[SHARED_FROM_KEY] = selectedOption.value
        }
        setModifiableFilters(newFilterValues)
      },
      [modifiableFilterValues]
    )

    const selectedMyList = allFilterValues[MY_LIST_FILTER_KEY]
    const selectedExhibitor = allFilterValues[EXHIBITOR_FILTER_KEY]
    const selectedSharedFrom = allFilterValues[SHARED_FROM_KEY]

    let selectedValue = defaultViewFromValue
    if (selectedMyList) {
      selectedValue = MY_LIST_FILTER_KEY
    } else if (selectedExhibitor) {
      selectedValue = EXHIBITOR_FILTER_KEY
    } else if (selectedSharedFrom) {
      selectedValue = selectedSharedFrom
    }
    selectedValue = resolveViewFromValue(selectedValue)

    return (
      <RendererComponent
        id={VIEW_FROM_FILTER_NODE_KEY_V2}
        defaultValue={defaultViewFromValue}
        label={translate(label)}
        options={viewFromOptionsWithSharedFrom}
        disabled={false}
        onSelect={onSelect}
        value={selectedValue}
        additionalProps={{ shouldForceUpdateOptimisticValue: true }}
      />
    )
  }

  const getDefaultValue = (): FilterValuesType | undefined => {
    // Get project data using shared utility function
    const { formValues, org } = getProjectData()

    if (!formValues || !org) {
      return undefined
    }

    // Get default ViewFrom filter value based on component selector owner and sharing status
    const defaultViewFromValue = getDynamicDefaultValue(formValues, org)

    // Return the appropriate filter values
    if (defaultViewFromValue === MY_LIST_FILTER_KEY) {
      return { [MY_LIST_FILTER_KEY]: true }
    } else if (defaultViewFromValue === EXHIBITOR_FILTER_KEY) {
      return { [EXHIBITOR_FILTER_KEY]: true }
    } else if (defaultViewFromValue !== DEFAULT_OPTION.value) {
      // This is a shared from org ID
      return { [SHARED_FROM_KEY]: defaultViewFromValue }
    }

    return undefined
  }

  return {
    key: VIEW_FROM_FILTER_NODE_KEY_V2,
    component: FilterComponent,
    getMatchedValueFromSpecsData: getDefaultValue,
  }
}

export default createViewFromFilterNode
